import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, HStack, Spinner, Text, Textarea, VStack, useToast } from "@chakra-ui/react";
import PersonChip from "./PersonChip";
import { fetchWorkItemActivity, commentOnWorkItem } from "../../services/workItemsService";
import { onWorkItemsChanged } from "../../utils/workItemsBus";
import { summaryKey } from "../../utils/workItemRefs";

const STEPS = {
  assigned: { verb: "assigned this to", to: true },
  reassigned: { verb: "reassigned this to", to: true },
  rejected: { verb: "rejected it" },
  submitted: { verb: "marked it done" },
  completed: { verb: "completed it" },
  sent_back: { verb: "sent it back" },
  reopened: { verb: "reopened it" },
  cancelled: { verb: "cancelled it" },
  unassigned: { verb: "unassigned it" },
  started: { verb: "moved it to In progress" },
  stopped: { verb: "moved it back to open" },
  // Its body lists what changed, one line each: not a message, so not quoted.
  edited: { verb: "edited the task", details: true },
};

// Each item's last-loaded feed, per viewer, kept in memory for this tab only
// (never in browser storage: comments can mention patients). Re-opening an
// item's Activity shows it at once while a fresh copy loads.
const feedCache = new Map();
const FEED_CACHE_MAX = 200;
const ACTIVITY_REFRESH_MS = 30000;

function feedKey(userId, ref) {
  return `${userId || ""}|${summaryKey(ref)}`;
}

function rememberFeed(key, feed) {
  feedCache.delete(key);
  feedCache.set(key, feed);
  if (feedCache.size > FEED_CACHE_MAX) feedCache.delete(feedCache.keys().next().value);
}

export function clearActivityCache() {
  feedCache.clear();
}

export function formatStamp(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function Person({ person }) {
  return <PersonChip person={person} size="xs" avatarSize="2xs" />;
}

function Entry({ entry }) {
  if (entry.event === "comment") {
    return (
      <Box bg="gray.50" _dark={{ bg: "gray.700" }} borderRadius="md" px={2} py={1.5}
           data-testid={`activity-entry-${entry.event}`}>
        <HStack spacing={1.5} flexWrap="wrap">
          <Person person={entry.actor} />
          <Text as="span" fontSize="xs" color="gray.500">{formatStamp(entry.created_at)}</Text>
        </HStack>
        <Text fontSize="sm" whiteSpace="pre-wrap" mt={0.5}>{entry.body}</Text>
      </Box>
    );
  }
  const step = STEPS[entry.event] || { verb: entry.event };
  return (
    <Box fontSize="xs" color="gray.600" _dark={{ color: "gray.300" }} data-testid={`activity-entry-${entry.event}`}>
      <HStack spacing={1.5} flexWrap="wrap">
        <Person person={entry.actor} />
        <Text as="span">{step.verb}</Text>
        {step.to && <Person person={entry.to} />}
        <Text as="span" color="gray.500">· {formatStamp(entry.created_at)}</Text>
      </HStack>
      {entry.body && step.details && (
        <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.300" }} whiteSpace="pre-wrap" pl={2} mt={0.5}>
          {entry.body}
        </Text>
      )}
      {entry.body && !step.details && (
        <Text fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }} fontStyle="italic" pl={2} mt={0.5}>
          “{entry.body}”
        </Text>
      )}
    </Box>
  );
}

// A task's or order's timeline and comments, oldest first, with a comment box
// for the person it's assigned to and the person who assigned it.
// item: { kind, id, schema?, assignedTo, assignedBy }
// focusComment: bump it (a host's "Comment" button) to put the cursor in the box.
// onCommentCount(n): told how many comments it shows, so the host's 💬 count
// keeps up with comments from other people.
export default function WorkItemActivity({ item, currentUserId, maxH = "280px", focusComment = 0, onCommentCount }) {
  const { kind, id, schema, assignedTo, assignedBy } = item;
  const key = feedKey(currentUserId, { kind, id, schema });
  // What this item showed last time: drawn at once, then refreshed.
  const [entries, setEntries] = useState(() => feedCache.get(key)?.entries ?? null);
  // null until the server answers: whether comments work depends on the
  // 2026-10-02 migration, so the box must not show (and vanish) before then.
  const [ready, setReady] = useState(() => feedCache.get(key)?.ready ?? null);
  const boxRef = useRef(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const res = await fetchWorkItemActivity({ kind, id, schema });
      const feed = { entries: res?.activity || [], ready: res?.ready !== false };
      rememberFeed(key, feed);
      setEntries(feed.entries);
      setReady(feed.ready);
      setError(null);
    } catch (err) {
      setError(err?.message || "Couldn't load the activity");
    }
  }, [kind, id, schema, key]);

  useEffect(() => {
    load();
  }, [load]);
  // While it's open, pick up other people's comments and steps.
  useEffect(() => {
    const timer = setInterval(load, ACTIVITY_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const onCommentCountRef = useRef(onCommentCount);
  onCommentCountRef.current = onCommentCount;
  useEffect(() => {
    if (entries) onCommentCountRef.current?.(entries.filter((e) => e.event === "comment").length);
  }, [entries]);
  // A step or comment on this item, from this window or another view → refresh.
  useEffect(
    () =>
      onWorkItemsChanged((e) => {
        const d = e?.detail;
        if (!d?.id || (d.kind === kind && d.id === id)) load();
      }),
    [load, kind, id],
  );

  const canComment = ready === true && !!currentUserId && (currentUserId === assignedTo || currentUserId === assignedBy);

  useEffect(() => {
    if (focusComment && canComment) boxRef.current?.focus();
  }, [focusComment, canComment]);

  const post = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const res = await commentOnWorkItem({ kind, id, schema }, currentUserId, body);
      if (res?.entry) {
        const next = [...(entries || []), res.entry];
        rememberFeed(key, { entries: next, ready: true });
        setEntries(next);
      }
      setDraft("");
    } catch (err) {
      toast({ title: "Couldn't post the comment", description: err?.message, status: "error", duration: 5000 });
    } finally {
      setPosting(false);
    }
  };

  return (
    <Box data-testid={`activity-${kind}-${id}`} mt={2} pt={2} borderTopWidth="1px" borderColor="gray.100">
      {error && <Text fontSize="sm" color="red.500">{error}</Text>}
      {!error && entries === null && <Spinner size="sm" />}
      {entries && (
        <VStack align="stretch" spacing={1.5} maxH={maxH} overflowY="auto">
          {entries.map((entry, i) => <Entry key={entry.id || `${entry.event}-${i}`} entry={entry} />)}
        </VStack>
      )}
      {ready === false && (
        <Text fontSize="xs" color="orange.600" mt={1}>
          Comments and the full timeline need the 2026-10-02 migration.
        </Text>
      )}
      {canComment && (
        <HStack mt={2} align="flex-end">
          <Textarea ref={boxRef} size="sm" rows={2} value={draft} maxLength={4000} placeholder="Add a comment"
                    aria-label="Add a comment"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post(); }} />
          <Button size="sm" colorScheme="blue" onClick={post} isLoading={posting} isDisabled={!draft.trim()}>
            Comment
          </Button>
        </HStack>
      )}
    </Box>
  );
}
